import React, { useState, useEffect } from 'react';
import { adminAPI, getWebsiteImageUrl } from '../../services/api.jsx';

const WebsiteImagesManager = () => {
    const [images, setImages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [uploading, setUploading] = useState({});
    const [error, setError] = useState('');

    useEffect(() => {
        fetchImages();
    }, []);

    const fetchImages = async () => {
        try {
            setLoading(true);
            const response = await adminAPI.getWebsiteImages();
            setImages(response.data);
        } catch (error) {
            console.error('Error fetching website images:', error);
            setError('Fehler beim Laden der Website-Bilder');
        } finally {
            setLoading(false);
        }
    };

    const handleImageUpload = async (imageName, file) => {
        if (!file) return;

        setUploading(prev => ({ ...prev, [imageName]: true }));
        setError('');

        try {
            const response = await adminAPI.uploadWebsiteImage(imageName, file);
            setImages(prev => prev.map(img =>
                img.name === imageName ? response.data : img
            ));
        } catch (error) {
            console.error('Error uploading image:', error);
            setError(`Fehler beim Hochladen: ${error.response?.data?.detail || error.message}`);
        } finally {
            setUploading(prev => ({ ...prev, [imageName]: false }));
        }
    };

    const handleFileSelect = (imageName, event) => {
        const file = event.target.files[0];
        if (file) {
            handleImageUpload(imageName, file);
        }
    };

    const getImageDisplayName = (name) => {
        const displayNames = {
            'hero_background': 'Hero Hintergrundbild'
        };
        return displayNames[name] || name;
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center">
                <h2 className="text-2xl font-bold text-neutral-950">Website-Bilder verwalten</h2>
                <button
                    onClick={fetchImages}
                    className="bg-neutral-600 hover:bg-neutral-700 text-white px-4 py-2 rounded-md font-medium transition-colors"
                >
                    Aktualisieren
                </button>
            </div>

            {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md">
                    {error}
                </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {images.map((image) => (
                    <div key={image.id} className="bg-white rounded-lg border border-neutral-200 p-6 shadow-sm">
                        <div className="space-y-4">
                            <div>
                                <h3 className="text-lg font-semibold text-neutral-950">
                                    {getImageDisplayName(image.name)}
                                </h3>
                                <p className="text-sm text-neutral-600 mt-1">
                                    {image.description || 'Keine Beschreibung verfügbar'}
                                </p>
                            </div>

                            {/* Image Preview */}
                            <div className="relative">
                                <div className="w-full h-48 bg-neutral-100 rounded-lg flex items-center justify-center overflow-hidden">
                                    {image.image_path ? (
                                        <img
                                            src={getWebsiteImageUrl(image.image_path)}
                                            alt={getImageDisplayName(image.name)}
                                            className="w-full h-full object-cover"
                                            onError={(e) => {
                                                console.error('Image load error for:', image.image_path, 'URL:', getWebsiteImageUrl(image.image_path));
                                                e.target.style.display = 'none';
                                                e.target.nextSibling.style.display = 'flex';
                                            }}
                                            onLoad={(e) => {
                                                console.log('Image loaded successfully:', image.image_path, 'URL:', getWebsiteImageUrl(image.image_path));
                                            }}
                                        />
                                    ) : null}
                                    <div className={`w-full h-full flex items-center justify-center text-neutral-500 ${image.image_path ? 'hidden' : ''}`}>
                                        <div className="text-center">
                                            <svg className="w-12 h-12 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                            </svg>
                                            <p className="text-sm">Kein Bild</p>
                                            {image.image_path && (
                                                <p className="text-xs text-red-500 mt-1">Fehler beim Laden: {image.image_path}</p>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Upload Button */}
                            <div className="space-y-2">
                                <label className="block">
                                    <input
                                        type="file"
                                        accept="image/*"
                                        onChange={(e) => handleFileSelect(image.name, e)}
                                        disabled={uploading[image.name]}
                                        className="hidden"
                                    />
                                    <span className={`inline-flex items-center justify-center w-full px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white transition-colors cursor-pointer ${uploading[image.name]
                                        ? 'bg-neutral-400 cursor-not-allowed'
                                        : 'bg-primary-600 hover:bg-primary-700'
                                        }`}>
                                        {uploading[image.name] ? (
                                            <>
                                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                                                Wird hochgeladen...
                                            </>
                                        ) : (
                                            <>
                                                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                                                </svg>
                                                {image.image_path ? 'Bild ersetzen' : 'Bild hochladen'}
                                            </>
                                        )}
                                    </span>
                                </label>

                                {image.image_path && (
                                    <div className="text-xs text-neutral-600">
                                        <p>Aktuelles Bild: {image.image_path.split('/').pop()}</p>
                                        <p>Zuletzt geändert: {new Date(image.updated_at).toLocaleDateString('de-DE')}</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {images.length === 0 && (
                <div className="text-center py-12">
                    <div className="text-neutral-500">
                        <svg className="w-16 h-16 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                        <p>Keine Website-Bilder gefunden</p>
                    </div>
                </div>
            )}
        </div>
    );
};

export default WebsiteImagesManager;
